import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, X, Plus, BookOpen, Tag, Loader, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Layers } from 'lucide-react';
import { slamService, toAssessmentItem, SlamItem, SlamItemVersion, SlamSearchRequest, SlamPicklist, parseSlamVersions } from '../../../services/slamService';
import { AssessmentItem } from '../../../types';
import { useTranslation } from '../../../contexts/TranslationContext';

interface SlamItemPickerProps {
  onSelect: (items: AssessmentItem[]) => void;
  onClose: () => void;
  /** Pre-filter by grade from the lesson metadata */
  defaultGrade?: string;
}

export const SlamItemPicker: React.FC<SlamItemPickerProps> = ({ onSelect, onClose, defaultGrade }) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [grade, setGrade] = useState(defaultGrade || '');
  const [domain, setDomain] = useState('');
  const [skill, setSkill] = useState('');
  const [difficultyMin, setDifficultyMin] = useState('');
  const [difficultyMax, setDifficultyMax] = useState('');
  const [page, setPage] = useState(1);

  const [results, setResults] = useState<SlamItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [domains, setDomains] = useState<SlamPicklist[]>([]);
  const [grades, setGrades] = useState<SlamPicklist[]>([]);
  const [skills, setSkills] = useState<SlamPicklist[]>([]);
  const [difficulties, setDifficulties] = useState<SlamPicklist[]>([]);

  // selected: itemId → chosen SlamItemVersion
  const [selected, setSelected] = useState<Map<number, SlamItemVersion>>(new Map());
  // expanded: set of itemIds whose version panel is open
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  const LIMIT = 10;

  // Load picklists on mount
  useEffect(() => {
    slamService.getPicklists('domain').then(setDomains).catch(() => {});
    slamService.getPicklists('skill').then(setSkills).catch(() => {});
    slamService.getPicklists('difficulty').then(setDifficulties).catch(() => {});
    slamService.getPicklists('grade').then(setGrades).catch(() => {});
  }, []);

  const search = useCallback(async (pageNum = 1) => {
    setLoading(true);
    setError(null);
    try {
      const req: SlamSearchRequest = {
        query: query || undefined,
        grade: grade || undefined,
        domain: domain || undefined,
        skill: skill || undefined,
        difficulty_min: difficultyMin ? parseInt(difficultyMin) : undefined,
        difficulty_max: difficultyMax ? parseInt(difficultyMax) : undefined,
        page: pageNum,
        limit: LIMIT,
        include_content: true,
      };
      const res = await slamService.searchItems(req);
      setResults(res.items);
      setTotalCount(res.total_count);
      setHasMore(res.has_more);
      setPage(pageNum);
    } catch (e: any) {
      setError(e.message || t('slamPicker.errorSearch'));
    } finally {
      setLoading(false);
    }
  }, [query, grade, domain, skill, difficultyMin, difficultyMax, t]);

  // Initial search
  useEffect(() => {
    search(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleSelect = (item: SlamItem, version: SlamItemVersion) => {
    setSelected(prev => {
      const next = new Map(prev);
      if (next.has(item.id)) {
        next.delete(item.id);
      } else {
        next.set(item.id, version);
      }
      return next;
    });
  };

  const selectVersion = (item: SlamItem, version: SlamItemVersion) => {
    setSelected(prev => {
      const next = new Map(prev);
      // Selecting a version always marks the item as selected with that version
      next.set(item.id, version);
      return next;
    });
  };

  const toggleExpand = (itemId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpanded(prev => {
      const next = new Set(prev);
      next.has(itemId) ? next.delete(itemId) : next.add(itemId);
      return next;
    });
  };

  const handleConfirm = () => {
    const assessmentItems = Array.from(selected.entries())
      .map(([itemId, version]) => {
        const item = results.find(r => r.id === itemId);
        if (!item) return null;
        return toAssessmentItem(item, version);
      })
      .filter((x): x is ReturnType<typeof toAssessmentItem> => x !== null);
    onSelect(assessmentItems as AssessmentItem[]);
  };

  const totalPages = Math.ceil(totalCount / LIMIT);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center space-x-2">
            <BookOpen className="w-5 h-5 text-blue-600" />
            <h2 className="text-lg font-semibold text-gray-900">{t('slamPicker.title')}</h2>
            {selected.size > 0 && (
              <span className="bg-blue-600 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                {t('slamPicker.selected', { count: selected.size })}
              </span>
            )}
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Filters */}
        <div className="p-4 border-b border-gray-100 bg-gray-50">
          <div className="flex flex-wrap gap-3">
            {/* Search */}
            <div className="flex-1 min-w-48 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={query}
                onChange={e => setQuery(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && search(1)}
                placeholder={t('slamPicker.searchPlaceholder')}
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Grade */}
            {grades.length > 0 ? (
              <select
                value={grade}
                onChange={e => setGrade(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">{t('slamPicker.allGrades')}</option>
                {grades.map(g => (
                  <option key={g.id} value={g.value}>{g.label || g.value}</option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                value={grade}
                onChange={e => setGrade(e.target.value)}
                placeholder={t('slamPicker.gradePlaceholder')}
                className="w-32 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            )}

            {/* Domain */}
            {domains.length > 0 ? (
              <select
                value={domain}
                onChange={e => setDomain(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">{t('slamPicker.allDomains')}</option>
                {domains.map(d => (
                  <option key={d.id} value={d.value}>{d.label || d.value}</option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                value={domain}
                onChange={e => setDomain(e.target.value)}
                placeholder={t('assessmentsSection.slamDomain')}
                className="w-36 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            )}

            {/* Skill */}
            {skills.length > 0 ? (
              <select
                value={skill}
                onChange={e => setSkill(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">{t('slamPicker.allSkills')}</option>
                {skills.map(s => (
                  <option key={s.id} value={s.value}>{s.label || s.value}</option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                value={skill}
                onChange={e => setSkill(e.target.value)}
                placeholder={t('assessmentsSection.slamSkill')}
                className="w-36 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            )}

            {/* Difficulty range */}
            <div className="flex items-center space-x-1">
              {difficulties.length > 0 ? (
                <select
                value={difficultyMin}
                onChange={e => setDifficultyMin(e.target.value)}
                className="px-2 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">{t('slamPicker.diffMin')}</option>
                    {difficulties.map(d => <option key={d.id} value={d.value}>{d.label || d.value}</option>)}
                  </select>
                ) : (
                  <input
                    type="text"
                    value={difficultyMin}
                    onChange={e => setDifficultyMin(e.target.value)}
                    placeholder={t('slamPicker.diffMin')}
                    className="w-24 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                )}
                {difficulties.length > 0 ? (
                  <select
                    value={difficultyMax}
                    onChange={e => setDifficultyMax(e.target.value)}
                    className="px-2 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">{t('slamPicker.diffMax')}</option>
                    {difficulties.map(d => <option key={d.id} value={d.value}>{d.label || d.value}</option>)}
                  </select>
                ) : (
                  <input
                    type="text"
                    value={difficultyMax}
                    onChange={e => setDifficultyMax(e.target.value)}
                    placeholder={t('slamPicker.diffMax')}
                    className="w-24 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                )}
              </div>

            <button
              onClick={() => search(1)}
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors flex items-center space-x-1"
            >
              {loading ? <Loader className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              <span>{t('slamPicker.search')}</span>
            </button>
          </div>
        </div>

        {/* Results */}
        <div className="flex-1 overflow-y-auto p-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 mb-4 text-sm">
              {error}
            </div>
          )}

          {loading && results.length === 0 && (
            <div className="flex items-center justify-center py-16">
              <Loader className="w-8 h-8 animate-spin text-blue-500" />
            </div>
          )}

          {!loading && results.length === 0 && !error && (
            <div className="text-center py-16 text-gray-500">
              <BookOpen className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>{t('slamPicker.noResults')}</p>
            </div>
          )}

          <div className="space-y-3">
            <AnimatePresence>
              {results.map(item => {
                const isSelected = selected.has(item.id);
                const isExpanded = expanded.has(item.id);
                const versions = parseSlamVersions(item);
                const hasAlternatives = versions.length > 1;
                const chosenVersion = selected.get(item.id) ?? versions[0];
                const difficultyLabel = item.difficulty
                  ? (t(`slamPicker.difficulty${item.difficulty}`) || t('slamPicker.difficultyLevel', { level: item.difficulty }))
                  : null;

                return (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    className={`border-2 rounded-lg transition-all ${
                      isSelected
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-blue-300'
                    }`}
                  >
                    {/* Main row */}
                    <div className="p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div
                          className="flex-1 min-w-0 cursor-pointer"
                          onClick={() => toggleSelect(item, chosenVersion)}
                        >
                          {/* Badges */}
                          <div className="flex flex-wrap gap-1.5 mb-2">
                            {item.domain && (
                              <span className="flex items-center space-x-1 bg-blue-100 text-blue-700 text-xs px-2 py-0.5 rounded-full">
                                <Tag className="w-3 h-3" />
                                <span>{item.domain}</span>
                              </span>
                            )}
                            {item.title && (
                              <span className="flex items-center space-x-1 bg-blue-100 text-blue-700 text-xs px-2 py-0.5 rounded-full">
                                <BookOpen className="w-3 h-3" />
                                <span>{item.title}</span>
                              </span>
                            )}
                            {item.skill && (
                              <span className="bg-purple-100 text-purple-700 text-xs px-2 py-0.5 rounded-full">{item.skill}</span>
                            )}
                            {item.grade && (
                              <span className="bg-green-100 text-green-700 text-xs px-2 py-0.5 rounded-full">{item.grade}</span>
                            )}
                            {difficultyLabel && (
                              <span className="bg-yellow-100 text-yellow-700 text-xs px-2 py-0.5 rounded-full">
                                {difficultyLabel}
                              </span>
                            )}
                            {item.language && (
                              <span className="bg-gray-100 text-gray-600 text-xs px-2 py-0.5 rounded-full uppercase">{item.language}</span>
                            )}
                            {hasAlternatives && (
                              <span className="flex items-center space-x-1 bg-indigo-100 text-indigo-700 text-xs px-2 py-0.5 rounded-full">
                                <Layers className="w-3 h-3" />
                                <span>{t('slamPicker.versionsCount', { count: versions.length })}</span>
                              </span>
                            )}
                          </div>

                          {/* Title */}
                          <p className="font-medium text-gray-900 text-sm mb-1">
                            {item.title || `Item #${item.id}`}
                          </p>

                          {/* Description preview of chosen version */}
                          {chosenVersion.description && (
                            <div
                              className="text-xs text-gray-600 line-clamp-2"
                              dangerouslySetInnerHTML={{ __html: chosenVersion.description }}
                            />
                          )}

                          {/* Version indicator when selected and has alternatives */}
                          {isSelected && hasAlternatives && (
                            <p className="text-xs text-blue-600 mt-1 font-medium">
                              {t('slamPicker.versionChosen', { n: chosenVersion.index + 1, total: versions.length })}
                            </p>
                          )}
                        </div>

                        <div className="flex items-center gap-2 flex-shrink-0">
                          {/* Expand versions button — only when there are alternatives */}
                          {hasAlternatives && (
                            <button
                              type="button"
                              onClick={e => toggleExpand(item.id, e)}
                              className={`p-1.5 rounded-lg transition-colors text-xs flex items-center gap-1 ${
                                isExpanded
                                  ? 'bg-indigo-100 text-indigo-700'
                                  : 'hover:bg-gray-100 text-gray-500'
                              }`}
                              title={t('slamPicker.chooseVersion')}
                            >
                              <Layers className="w-3.5 h-3.5" />
                              {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                            </button>
                          )}

                          {/* Checkbox */}
                          <div
                            className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors cursor-pointer ${
                              isSelected ? 'border-blue-500 bg-blue-500' : 'border-gray-300 hover:border-blue-400'
                            }`}
                            onClick={() => toggleSelect(item, chosenVersion)}
                          >
                            {isSelected && <span className="text-white text-xs font-bold">✓</span>}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Version selector panel — always shown when expanded, lists all versions */}
                    <AnimatePresence>
                      {isExpanded && hasAlternatives && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.2 }}
                          className="overflow-hidden border-t border-indigo-100"
                        >
                          <div className="p-3 bg-indigo-50">
                            <p className="text-xs font-semibold text-indigo-700 mb-2 flex items-center gap-1">
                              <Layers className="w-3.5 h-3.5" />
                              {t('slamPicker.chooseVersion')}
                            </p>
                            <div className="space-y-2">
                              {versions.map(v => {
                                const isChosen = chosenVersion.index === v.index;
                                return (
                                  <button
                                    key={v.index}
                                    type="button"
                                    onClick={e => {
                                      e.stopPropagation();
                                      selectVersion(item, v);
                                    }}
                                    className={`w-full text-left rounded-lg border-2 p-3 transition-all ${
                                      isChosen && isSelected
                                        ? 'border-blue-500 bg-white'
                                        : isChosen
                                          ? 'border-indigo-300 bg-white'
                                          : 'border-transparent bg-white hover:border-indigo-300'
                                    }`}
                                  >
                                    <div className="flex items-start justify-between gap-2">
                                      <div className="flex-1 min-w-0">
                                        <p className="text-xs font-semibold text-gray-500 mb-1">
                                          {v.index === 0
                                            ? t('slamPicker.versionMain')
                                            : t('slamPicker.versionAlt', { n: v.index })}
                                        </p>
                                        <div
                                          className="text-sm text-gray-800 line-clamp-3"
                                          dangerouslySetInnerHTML={{ __html: v.description || '—' }}
                                        />
                                        {v.response && (
                                          <p className="text-xs text-green-700 mt-1">
                                            <span className="font-medium">{t('slamPicker.response')} : </span>
                                            <span dangerouslySetInnerHTML={{ __html: v.response }} />
                                          </p>
                                        )}
                                      </div>
                                      <div className={`flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                                        isChosen && isSelected
                                          ? 'border-blue-500 bg-blue-500'
                                          : isChosen
                                            ? 'border-indigo-400 bg-indigo-100'
                                            : 'border-gray-300'
                                      }`}>
                                        {isChosen && isSelected && <span className="text-white text-xs font-bold">✓</span>}
                                        {isChosen && !isSelected && <span className="text-indigo-600 text-xs font-bold">·</span>}
                                      </div>
                                    </div>
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>
        </div>

        {/* Pagination */}
        {totalCount > LIMIT && (
          <div className="flex items-center justify-center space-x-3 px-4 py-2 border-t border-gray-100 text-sm text-gray-600">
            <button
              onClick={() => search(page - 1)}
              disabled={page <= 1 || loading}
              className="p-1 hover:bg-gray-100 rounded disabled:opacity-40"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span>{t('slamPicker.page', { page, total: totalPages, count: totalCount })}</span>
            <button
              onClick={() => search(page + 1)}
              disabled={!hasMore || loading}
              className="p-1 hover:bg-gray-100 rounded disabled:opacity-40"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t border-gray-200 bg-gray-50">
          <span className="text-sm text-gray-500">
            {selected.size === 0
              ? t('slamPicker.selectHint')
              : selected.size === 1
                ? t('slamPicker.selectedHint', { count: selected.size })
                : t('slamPicker.selectedHintPlural', { count: selected.size })}
          </span>
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              type="button"
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-100 transition-colors"
            >
              {t('slamPicker.cancel')}
            </button>
            <button
              onClick={handleConfirm}
              type="button"
              disabled={selected.size === 0}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
            >
              <Plus className="w-4 h-4" />
              <span>
                {selected.size > 0
                  ? t('slamPicker.addCount', { count: selected.size })
                  : t('slamPicker.add')}
              </span>
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};
